import { OidcClient, WebStorageStateStore } from 'oidc-client';
export { CordovaIFrameNavigator, CordovaPopupNavigator, InMemoryWebStorage, Log, OidcClient, SessionMonitor, User, UserManager, Version, WebStorageStateStore } from 'oidc-client';
import { JWK } from 'node-jose';
import JWT from 'jsonwebtoken';
import { v4 } from 'uuid';
import formurlencoded from 'form-urlencoded';

function processErrorResponse(responseBody, options) {
    var _a, _b, _c, _d;
    if (responseBody.error === "invalid_redirect_uri") {
        throw new Error(`Dynamic client registration failed: the provided redirect uri [${(_a = options.redirectUrl) === null || _a === void 0 ? void 0 : _a.toString()}] is invalid - ${(_b = responseBody.error_description) !== null && _b !== void 0 ? _b : ""}`);
    }
    if (responseBody.error === "invalid_client_metadata") {
        throw new Error(`Dynamic client registration failed: the provided client metadata ${JSON.stringify(options)} is invalid - ${(_c = responseBody.error_description) !== null && _c !== void 0 ? _c : ""}`);
    }
    throw new Error(`Dynamic client registration failed: ${responseBody.error} - ${(_d = responseBody.error_description) !== null && _d !== void 0 ? _d : ""}`);
}
function validateRegistrationResponse(responseBody, options) {
    if (responseBody.client_id === undefined) {
        throw new Error(`Dynamic client registration failed: no client_id has been found on ${JSON.stringify(responseBody)}`);
    }
    if (options.redirectUrl &&
        (responseBody.redirect_uris === undefined ||
            responseBody.redirect_uris[0] !== options.redirectUrl.toString())) {
        throw new Error(`Dynamic client registration failed: the returned redirect URIs ${JSON.stringify(responseBody.redirect_uris)} don't match the provided ${JSON.stringify([
            options.redirectUrl.toString(),
        ])}`);
    }
}
async function registerClient(options, issuerConfig) {
    var _a;
    const config = {
        client_name: options.clientName,
        application_type: "web",
        redirect_uris: [(_a = options.redirectUrl) === null || _a === void 0 ? void 0 : _a.toString()],
        subject_type: "pairwise",
        token_endpoint_auth_method: "client_secret_basic",
        code_challenge_method: "S256",
    };
    if (!issuerConfig.registrationEndpoint) {
        throw new Error("Dynamic Registration could not be completed because the issuer has no registration endpoint.");
    }
    const headers = {
        "Content-Type": "application/json",
    };
    if (options.registrationAccessToken) {
        headers.Authorization = `Bearer ${options.registrationAccessToken}`;
    }
    const registerResponse = await fetch(issuerConfig.registrationEndpoint.toString(), {
        method: "POST",
        headers,
        body: JSON.stringify(config),
    });
    if (registerResponse.ok) {
        const responseBody = await registerResponse.json();
        validateRegistrationResponse(responseBody, options);
        return {
            clientId: responseBody.client_id,
            clientSecret: responseBody.client_secret,
        };
    }
    if (registerResponse.status === 400) {
        processErrorResponse(await registerResponse.json(), options);
    }
    throw new Error(`Dynamic client registration failed: the server returned ${registerResponse.status} ${registerResponse.statusText} - ${await registerResponse.text()}`);
}

async function signJwt(payload, key, options) {
    const parsedKey = await JWK.asKey(key);
    const convertedKey = parsedKey.toPEM(true);
    const signed = JWT.sign(payload, convertedKey, options);
    return signed;
}
async function decodeJwt(token, key, options) {
    if (key) {
        const parsedKey = await JWK.asKey(key);
        const convertedKey = parsedKey.toPEM(false);
        return JWT.verify(token, convertedKey, options);
    }
    return JWT.decode(token);
}
async function privateJwkToPublicJwk(key) {
    return (await JWK.asKey(key, "public"));
}
function normalizeHttpUriClaim(audience) {
    const cleanedAudience = new URL(audience);
    cleanedAudience.hash = "";
    cleanedAudience.username = "";
    cleanedAudience.password = "";
    return cleanedAudience.toString();
}
async function createDpopHeader(audience, method, key) {
    return signJwt({
        htu: normalizeHttpUriClaim(audience),
        htm: method.toUpperCase(),
        jti: v4(),
    }, key, {
        header: {
            jwk: await privateJwkToPublicJwk(key),
            typ: "dpop+jwt",
        },
        algorithm: "ES256",
    });
}

async function generateJwk(kty, crvBitlength, parameters) {
    const key = await JWK.createKey(kty, crvBitlength, parameters);
    return key.toJSON(true);
}
async function generateJwkForDpop() {
    return generateJwk("EC", "P-256", { alg: "ES256" });
}
async function generateJwkRsa() {
    return generateJwk("RSA");
}

function hasAccessToken(value) {
    return value.access_token && typeof value.access_token === "string";
}
function hasIdToken(value) {
    return value.id_token && typeof value.id_token === "string";
}
function hasRefreshToken(value) {
    return value.refresh_token && typeof value.refresh_token === "string";
}
function hasTokenType(value) {
    return value.token_type && typeof value.token_type === "string";
}
function isWebIdOidcIdToken(token) {
    return ((token.sub &&
        typeof token.sub === "string" &&
        token.iss &&
        typeof token.iss === "string" &&
        !token.webid) ||
        typeof token.webid === "string");
}
async function deriveWebIdFromIdToken(idToken) {
    const decoded = await decodeJwt(idToken);
    if (!isWebIdOidcIdToken(decoded)) {
        throw new Error(`Invalid ID token: ${JSON.stringify(decoded)} is missing 'sub' or 'iss' claims`);
    }
    if (decoded.webid) {
        return decoded.webid;
    }
    try {
        new URL(decoded.sub);
    }
    catch (error) {
        throw new Error(`Cannot extract WebID from ID token: the ID token returned by [${decoded.iss}] has no 'webid' claim, nor an IRI-like 'sub' claim: [${decoded.sub}]. Attempting to construct a URL from the 'sub' claim threw: ${error}`);
    }
    return decoded.sub;
}
function validatePreconditions(issuer, data) {
    if (data.grantType &&
        (!issuer.grantTypesSupported ||
            !issuer.grantTypesSupported.includes(data.grantType))) {
        throw new Error(`The issuer [${issuer.issuer}] does not support the [${data.grantType}] grant`);
    }
    if (!issuer.tokenEndpoint) {
        throw new Error(`This issuer [${issuer.issuer}] does not have a token endpoint`);
    }
}
function validateTokenEndpointResponse(tokenResponse, dpop) {
    if (!hasAccessToken(tokenResponse)) {
        throw new Error(`Invalid token endpoint response (missing the field 'access_token'): ${JSON.stringify(tokenResponse)}`);
    }
    if (!hasIdToken(tokenResponse)) {
        throw new Error(`Invalid token endpoint response (missing the field 'id_token'): ${JSON.stringify(tokenResponse)}.`);
    }
    if (!hasTokenType(tokenResponse)) {
        throw new Error(`Invalid token endpoint response (missing the field 'token_type'): ${JSON.stringify(tokenResponse)}`);
    }
    if (!dpop && tokenResponse.token_type.toLowerCase() !== "bearer") {
        throw new Error(`Invalid token endpoint response: requested a [Bearer] token, but got a 'token_type' value of [${tokenResponse.token_type}].`);
    }
    return tokenResponse;
}
async function getTokens(issuer, client, data, dpop) {
    validatePreconditions(issuer, data);
    const headers = {
        "content-type": "application/x-www-form-urlencoded",
    };
    let dpopJwk;
    if (dpop) {
        dpopJwk = await generateJwkForDpop();
        headers.DPoP = await createDpopHeader(issuer.tokenEndpoint, "POST", dpopJwk);
    }
    if (client.clientSecret) {
        headers.Authorization = `Basic ${btoa(`${client.clientId}:${client.clientSecret}`)}`;
    }
    const tokenRequestInit = {
        method: "POST",
        headers,
        body: formurlencoded({
            grant_type: data.grantType,
            redirect_uri: data.redirectUri,
            code: data.code,
            code_verifier: data.codeVerifier,
            client_id: client.clientId,
        }),
    };
    const rawTokenResponse = (await (await fetch(issuer.tokenEndpoint, tokenRequestInit)).json());
    const tokenResponse = validateTokenEndpointResponse(rawTokenResponse, dpop);
    const webId = await deriveWebIdFromIdToken(tokenResponse.id_token);
    return {
        accessToken: tokenResponse.access_token,
        idToken: tokenResponse.id_token,
        refreshToken: hasRefreshToken(tokenResponse)
            ? tokenResponse.refresh_token
            : undefined,
        webId,
        dpopJwk,
    };
}
async function getBearerToken(redirectUrl) {
    let signinResponse;
    try {
        signinResponse = await new OidcClient({
            response_mode: "query",
            loadUserInfo: false,
        }).processSigninResponse(redirectUrl);
    }
    catch (err) {
        throw new Error(`Problem handling Auth Code Grant (Flow) redirect - URL [${redirectUrl}]: ${err}`);
    }
    const webId = await deriveWebIdFromIdToken(signinResponse.id_token);
    return {
        accessToken: signinResponse.access_token,
        idToken: signinResponse.id_token,
        webId,
        refreshToken: signinResponse.refresh_token,
    };
}
async function getDpopToken(issuer, client, data) {
    return getTokens(issuer, client, data, true);
}

function removeOidcQueryParam(redirectUrl) {
    const cleanedUrl = new URL(redirectUrl);
    cleanedUrl.searchParams.delete("code");
    cleanedUrl.searchParams.delete("state");
    return cleanedUrl.toString();
}
async function clearOidcPersistentStorage() {
    const client = new OidcClient({
        response_mode: "query",
    });
    await client.clearStaleState(new WebStorageStateStore({}));
    const myStorage = window.localStorage;
    const itemsToRemove = [];
    for (let i = 0; i <= myStorage.length; i += 1) {
        const key = myStorage.key(i);
        if (key &&
            (key.match(/^oidc\..+$/) ||
                key.match(/^solidClientAuthenticationUser:.+$/))) {
            itemsToRemove.push(key);
        }
    }
    itemsToRemove.forEach((key) => myStorage.removeItem(key));
}

export { clearOidcPersistentStorage, createDpopHeader, decodeJwt, generateJwkForDpop, generateJwkRsa, getBearerToken, getDpopToken, privateJwkToPublicJwk, registerClient, removeOidcQueryParam, signJwt };
